/** English messages. Keys are stable; {placeholders} are filled by t(). */
export const en: Record<string, string> = {
  // Start screen
  'start.tagline': 'Design broadcast graphics for PRISM as EBU OGraf v1 packages.',
  'start.new': 'New graphic',
  'start.name': 'Name',
  'start.namePlaceholder': 'Untitled graphic',
  'start.create': 'Create blank graphic',
  'start.examples': 'Examples',
  'start.examplesHint': 'The conformance samples, opened as new drafts.',
  'templates.title': 'New from template',
  'right.brand': 'Brand',
  'inspector.anchorTo': 'Anchor to',
  'anchor.toParent': 'Its group',
  'anchor.toCanvas': 'The canvas',
  'inspector.mask.fixedContent': 'Content stays when the mask moves',
  'inspector.mask.hint': 'Children show only inside the mask. Animate its inset (Wipe, Reveal) to reveal them, or its position to slide it over them.',
  'inspector.video.mp4': 'MP4 (H.264): no alpha channel.',
  'inspector.video.alpha': 'WebM with alpha.',
  'inspector.video.opaque': 'WebM without alpha.',
  'inspector.video.timing': 'It starts when the graphic plays in; the preview and PRISM seek it frame by frame.',
  'inspector.effects': 'Effects',
  'inspector.blendMode': 'Blend mode',
  'inspector.blendWarning': 'Blend modes mix only with the graphic’s own pixels, not with the video it is keyed over.',
  'blend.normal': 'Normal',
  'blend.multiply': 'Multiply',
  'blend.screen': 'Screen',
  'blend.overlay': 'Overlay',
  'blend.darken': 'Darken',
  'blend.lighten': 'Lighten',
  'blend.difference': 'Difference',
  'blend.exclusion': 'Exclusion',
  'problem.blendMode': '{name} uses the “{mode}” blend mode. Over a transparent graphic it blends only with the graphic itself, not with the video it is keyed over, so it looks different on air.',
  'problem.blurLarge': '{name} blurs a large area: expensive to play in real time at 50 fps. Keep blurred areas small.',
  'problem.video.mp4': '{name} is MP4 (H.264): it has no alpha channel, so it covers what is behind it, and renderers without H.264 cannot play it. Use WebM (VP9 with alpha).',
  'problem.video.opaque': '{name} is a WebM without an alpha channel: it covers what is behind it.',
  'problem.video.large': '{name} is {size} MB: it goes into every package and loads before the first frame. Shorten or compress it.',
  'problem.packageLarge': 'The package will be about {size} MB, more than the {limit} MB limit. Large media slows saving and loading in PRISM.',
  'timeline.colorSpace': 'Colour blending: {space} (click to switch)',
  'timeline.colorSpaceHint': 'How this colour track blends between keyframes: sRGB (classic; can look muddy between saturated colours) or OKLab (even lightness and hue).',
  'brand.title': 'Brand kit',
  'brand.hintView': 'Your organisation’s colours, text styles, logos and fonts. Select elements, then click a colour or a text style to apply it; click a logo to add it.',
  'brand.hintEdit': 'Your organisation’s colours, text styles, logos and fonts. Select elements, then click a colour or a text style to apply it; click a logo to add it. You can change the kit for the whole workspace.',
  'brand.colors': 'Colours',
  'brand.noColors': 'No brand colours yet.',
  'brand.applyColor': 'Apply {name} to the selection',
  'brand.remove': 'Remove {name} from the brand kit',
  'brand.newColor': 'New brand colour',
  'brand.colorName': 'Colour name',
  'brand.addColor': 'Add the colour to the brand kit',
  'brand.textStyles': 'Text styles',
  'brand.noTextStyles': 'No text styles yet.',
  'brand.applyStyle': 'Apply {name} to the selected text',
  'brand.selectText': 'Select a text element to apply a text style',
  'brand.saveStyle': 'Save the selected text as a text style',
  'brand.styleNamePrompt': 'Name of the text style',
  'brand.styleDefaultName': 'Text style',
  'brand.logos': 'Logos',
  'brand.noLogos': 'No brand logos yet.',
  'brand.addLogo': 'Add {name} to the graphic',
  'brand.addFromLibrary': 'Add from the library',
  'brand.fonts': 'Fonts',
  'brand.noFonts': 'No brand fonts yet.',
  'brand.fontsHint': 'Brand fonts are listed first wherever you pick a font, and new templates use the first one.',
  'font.brandGroup': 'Brand',
  'templates.hint': 'Starter graphics built to survive real data. Each opens as a new draft you can change freely.',
  'templates.blank': 'Blank graphic',
  'templates.blankHint': 'An empty canvas in the format chosen above.',
  'templates.fields': '{n} fields',
  'templates.applyBrand': 'Use the brand kit (colours, font, logo)',
  'templates.allShapes': 'All shapes, not only {shape}',
  'templates.pickTitle': 'New graphic',
  'templates.pickHint': 'Start from a template or a blank canvas.',
  'embedded.resumeTitle': 'You have unsaved changes to this graphic',
  'embedded.resumeBody': 'Your draft of version {version} was last changed {when}. Continue it, or start again from the version in PRISM (your changes are discarded).',
  'embedded.resumeContinue': 'Continue my draft',
  'embedded.resumeFresh': 'Start from PRISM’s version',
  'start.drafts': 'Drafts',
  'start.noDrafts': 'No drafts yet.',
  'start.revision': 'revision {n}',
  'start.delete': 'Delete {name}',
  'start.confirmDelete': 'Delete the draft “{name}”?',

  // Editor shell
  'editor.loading': 'Loading…',
  'editor.notFound': 'This draft does not exist or was deleted.',
  'editor.backToDrafts': 'Back to drafts',
  'error.noFont': 'Upload a font first: text needs a font from the asset library.',
  'left.label': 'Layers and assets',
  'right.label': 'Properties',
  'right.design': 'Design',
  'right.data': 'Data',

  // Top bar
  'top.back': 'Back to drafts',
  'top.name': 'Graphic name',
  'top.resolution': 'Resolution',
  'top.frameRate': 'Frame rate',
  'top.fps': '{fps} fps',
  'top.customRateOption': 'Custom rate…',
  'top.customRate': 'Frame rate as a ratio num/den (e.g. 30000/1001)',
  // Formats (PRISM's sequence formats)
  'format.group': '{shape} {aspect}',
  'format.group.custom': 'Custom',
  'format.shape.landscape': 'Landscape',
  'format.shape.vertical': 'Vertical',
  'format.shape.portrait': 'Portrait',
  'format.shape.square': 'Square',
  'format.shape.other': 'Custom shape',
  'format.shapeHint': '{aspect} · {shape}',
  'format.hd1080': '1920 × 1080 (HD)',
  'format.uhd': '3840 × 2160 (UHD)',
  'format.hd720': '1280 × 720',
  'format.vertical': '1080 × 1920',
  'format.portrait45': '1080 × 1350',
  'format.square': '1080 × 1080',
  'format.customSize': '{width} × {height}',
  'format.rateCustom': '{fps} fps (custom, {ratio})',
  'format.change': 'Change format…',
  'format.change.title': 'Change format',
  'format.change.from': 'Now {width} × {height} ({shape}) at {fps} fps.',
  'format.change.target': 'Format',
  'format.change.customNew': 'Custom size…',
  'format.change.content': 'Content',
  'format.change.fit': 'Scale and reposition',
  'format.change.keep': 'Keep size, reposition only',
  'format.change.size': 'Content size: {percent} %',
  'format.change.hint': 'Elements keep their place by their anchors: pinned to an edge they stay at that edge, centred ones stay centred, stretched ones fill the new size. Afterwards the Problems panel lists anything outside graphics-safe.',
  'format.change.preview': 'Preview at {width} × {height}',
  'format.change.cancel': 'Cancel',
  'format.change.apply': 'Change format',
  'format.add.title': 'Add layout',
  'format.add.from': 'A layout for another shape, adapted from the primary {width} × {height} ({shape}). It shares the content, fields and timing; position, size, font size and visibility can differ.',
  'format.add.apply': 'Add layout',
  'format.layout.title': 'Change layout {label}',
  'format.layout.from': 'Re-adapts the {label} layout from the primary {width} × {height}; its own position, size and font size changes are replaced.',
  'format.taken': 'The graphic has a layout of this shape already.',
  // Layouts (ADR 0037)
  'layouts.label': 'Layouts',
  'layouts.tab': '{label} layout',
  'layouts.primary': 'Primary',
  'layouts.add': 'Add layout…',
  'layouts.menu': 'Layout {label} options',
  'layouts.change': 'Change format…',
  'layouts.remove': 'Remove layout',
  'layouts.removeConfirm': 'Remove the {label} layout? Its own positions, sizes and font sizes are lost (undo brings it back).',
  'layouts.problems': '{count} problems in this layout',
  'layouts.editing': 'Editing {label} — changes to position, size, font size and visibility apply to this layout only. New elements, deletions, text, colours and timing apply to all layouts.',
  'layouts.overridden': 'Differs in {label}',
  'layouts.reset': 'Reset to primary',
  'layouts.resetHint': 'Use the primary layout’s value here again',
  'layouts.showHere': 'Show in this layout',
  'layouts.canvasHint': 'This layout is {width} × {height}. Its size changes with “Change format…”, which adapts it from the primary again.',
  'layouts.keyOverridden': 'Value differs in this layout',
  'layouts.preview': 'Layout',
  'layouts.choice': 'Layouts',
  'layouts.choicePrimary': 'Primary: {shape}',
  'layouts.option': '{label} · {width} × {height}',
  'layouts.previewHint': 'The package is loaded at this layout’s size (renderCharacteristics.resolution), so it chooses that layout.',
  'top.undo': 'Undo',
  'top.redo': 'Redo',
  'top.export': 'Export',
  'top.exporting': 'Exporting…',
  'top.exportHint': 'Download the OGraf package (.zip)',
  'top.preview': 'Preview',
  'top.previewHint': 'Preview the exported package in a sandboxed frame, as an OGraf renderer runs it',
  'top.check': 'Check',
  'top.checking': 'Checking…',
  'top.checkHint': 'Export, validate against the OGraf schema, run the headless smoke test and render the thumbnail',
  'preview.log.step': 'step {step}',
  'preview.title': 'Preview',
  'preview.frameTitle': 'Exported graphic (sandboxed)',
  'preview.exporting': 'Exporting the package…',
  'preview.loading': 'Loading the layout…',
  'preview.loadedPackage': 'Package {id} loaded: {size}, {files} files',
  'preview.size': 'Package {size} (limit {limit})',
  'preview.sizeHint': 'Zip size; {unpacked} unpacked',
  'preview.sizeOver': 'The package is larger than the configured limit',
  'preview.mode': 'Preview mode',
  'preview.mode.realtime': 'Real time',
  'preview.mode.timeline': 'Timeline',
  'preview.background': 'Background',
  'preview.bg.checker': 'Checkerboard',
  'preview.bg.black': 'Black',
  'preview.bg.reference': 'Reference still',
  'preview.bg.upload': 'Image…',
  'preview.bg.uploadHint': 'Use an image from your computer as the reference still (stays in this browser)',
  'preview.zoom': 'Zoom',
  'preview.zoom.fit': 'Fit',
  'preview.zoom.actual': '100%',
  'preview.reexport': 'Re-export',
  'preview.reexportHint': 'Save, export again and reload the package',
  'preview.load': 'Load',
  'preview.play': 'Play',
  'preview.next': 'Next step',
  'preview.goto': 'Go to step',
  'preview.gotoStep': 'Step',
  'preview.update': 'Update',
  'preview.stop': 'Stop',
  'preview.dispose': 'Dispose',
  'preview.skip': 'Skip animation',
  'preview.customActions': 'Custom actions',
  'preview.payload': 'Payload for {name} (JSON)',
  'preview.dataHint': 'Load and Update send the Data panel’s values.',
  'preview.clip': 'Mock PRISM clip',
  'preview.clipLength': 'Clip length (frames)',
  'preview.framesSeconds': '{s} s',
  'preview.clipDefault': 'Default',
  'preview.clipDefaultHint': 'In + 4 s hold + out, as PRISM proposes when the graphic is dropped',
  'preview.stingLength': 'A graphic without steps runs its own length; PRISM fixes the clip to it.',
  'preview.outPoint': 'Out point',
  'preview.noStop': 'none (the graphic ends by itself)',
  'preview.atFrame': 'frame {n} ({s} s)',
  'preview.stepMarks': 'Step marks',
  'preview.stepMark': 'Step mark {n}',
  'preview.updates': 'Updates',
  'preview.updateMark': 'Update {n}',
  'preview.addAtPlayhead': 'Add at playhead',
  'preview.addUpdateHint': 'An update at the playhead with the Data panel’s changed values',
  'preview.remove': 'Remove',
  'preview.none': 'None',
  'preview.mappingHint': 'Like PRISM: load (non-real time), then setActionsSchedule (play at 0, next step at each mark, updates, stop so the out completes on the last frame, which is transparent), then goToTime for every frame.',
  'preview.toStart': 'To start',
  'preview.prevFrame': 'Previous frame',
  'preview.nextFrame': 'Next frame',
  'preview.playTimeline': 'Play',
  'preview.pause': 'Pause',
  'preview.scrubber': 'Playhead (frame)',
  'preview.frame': 'frame',
  'preview.timecode': 'TC',
  'preview.log': 'Calls and console',
  'preview.clearLog': 'Clear',
  'check.title': 'Check',
  'check.valid': 'Ready: the package validates and passed the smoke test',
  'check.invalid': '{n} problem(s) found',
  'check.took': '{ms} ms',
  'check.errors': 'Errors',
  'check.warnings': 'Warnings',
  'check.size': 'Package size',
  'check.sizeOf': '{size} (limit {limit})',
  'check.sceneHash': 'Scene hash',
  'check.thumbnail': 'thumbnail.png, {w}×{h}, at {ms} ms (step-0 hold)',
  'check.thumbnailAlt': 'The package thumbnail',
  'check.smoke': 'Headless smoke test: {n} calls, {result}',
  'check.passed': 'passed',
  'check.failed': 'failed',
  'check.call': 'Call',
  'check.status': 'Result',
  'check.step': 'Step',
  'check.exportAnyway': 'Export anyway',
  'top.saveToPrism': 'Save to PRISM',
  'top.saveHint': 'Check the graphic and save it to PRISM as a new version',
  'top.saveStandalone': 'Open the editor from PRISM to save graphics there',
  'top.saveNoPermission': 'You do not have permission to save graphics in PRISM',
  'top.close': 'Close the editor',

  // PRISM
  'prism.saveNewTitle': 'Save to PRISM',
  'prism.saveVersionTitle': 'Save version {version} to PRISM',
  'prism.name': 'Name',
  'prism.description': 'Description',
  'prism.tags': 'Tags',
  'prism.tagsHint': 'Comma-separated, e.g. news, election',
  'prism.folder': 'Folder',
  'prism.noFolder': 'No folder',
  'prism.readOnlyFolder': 'read-only',
  'prism.noMetadataPermission': 'Description and tags need the “edit metadata” permission in PRISM.',
  'prism.saveNew': 'Save to PRISM',
  'prism.saveVersion': 'Save version {version}',
  'prism.saveAsCopy': 'Save as copy',
  'prism.cancel': 'Cancel',
  'prism.saving': 'Checking the graphic and saving it to PRISM…',
  'prism.savingTitle': 'Saving to PRISM',
  'prism.checkFailed': 'The graphic did not pass the check, so it was not saved. Fix these problems and try again:',
  'prism.forbidden': 'PRISM refused the save: {message}',
  'prism.draftConflict': 'This draft was changed in another tab; resolve that first.',
  'prism.conflictTitle': 'Someone saved a newer version',
  'prism.conflictBody': '{who} saved version {version} at {when}. Your changes are based on version {base}. Save yours as a copy, or overwrite (your version becomes the next one)?',
  'prism.someone': 'Someone',
  'prism.copyName': 'Name of the copy',
  'prism.overwrite': 'Overwrite (save as version {version})',
  'prism.closeTitle': 'Unsaved changes',
  'prism.closeBody': 'This graphic has changes that are not saved to PRISM.',
  'prism.discard': 'Discard changes',
  'prism.saveAndClose': 'Save and close',
  'prism.inPrism': 'PRISM v{version}',
  'prism.savedInPrism': 'Saved in PRISM',
  'prism.unsavedInPrism': 'Changes not saved to PRISM yet',
  'prism.expired': 'Session expired – read-only. Reopen the editor from PRISM.',
  'embedded.waiting': 'Waiting for PRISM…',
  'embedded.opening': 'Opening…',
  'embedded.expired': 'The editor session expired. Reopen the editor from PRISM.',
  'embedded.forbidden': 'PRISM does not allow this: {message}',
  'embedded.close': 'Close',
  'readonly.badge': 'Read-only',
  'readonly.close': 'Close',
  'readonly.loading': 'Loading the package…',
  'readonly.loaded': 'Loaded. Fill in the data and press Play.',
  'readonly.frame': 'Graphic preview',
  'readonly.data': 'Data',
  'readonly.play': 'Play',
  'readonly.next': 'Next step',
  'readonly.update': 'Update',
  'readonly.stop': 'Stop',
  'top.shortcuts': 'Keyboard shortcuts (?)',
  'top.dev': 'Developer settings',
  'app.version': 'v{version}',
  'app.versionTitle': 'What’s new in PRISM Graphics v{version}',
  'releases.title': 'PRISM Graphics {version}',
  'releases.search': 'Search the release notes',
  'releases.none': 'No release mentions “{query}”.',
  'releases.showDetails': 'Show details',
  'releases.hideDetails': 'Hide details',
  'releases.features': 'New',
  'releases.fixes': 'Fixed',

  // Save status
  'save.saved': 'All changes saved',
  'save.savedAgo': 'Saved {minutes} min ago',
  'save.saving': 'Saving…',
  'save.dirty': 'Unsaved changes',
  'save.error': 'Not saved – retrying',
  'save.conflict': 'Changed elsewhere',
  'conflict.title': 'This draft was changed elsewhere',
  'conflict.body': 'Someone (or another tab) saved revision {revision} at {when}. Reload their version, or overwrite it with yours.',
  'conflict.reload': 'Reload their version',
  'conflict.overwrite': 'Overwrite with mine',

  // Dev menu and view
  'dev.title': 'Developer settings',
  'dev.theme': 'Theme',
  'dev.themeDark': 'Dark',
  'dev.themeLight': 'Light',
  'dev.renderState': 'Canvas shows',
  'dev.stateHold': 'Step 1 hold (after the in animation)',
  'dev.stateDesign': 'Design values (no animation)',
  'dev.actionSafe': 'Action safe',
  'dev.actionSafe.short': 'Action',
  'dev.graphicsSafe': 'Graphics safe',
  'dev.graphicsSafe.short': 'Graphics',
  'dev.clearGuides': 'Remove all guides',
  'view.safe': 'Safe areas (Shift+S)',
  'view.cut': '4:3 centre cut (Shift+C)',
  'view.rulers': 'Rulers',
  'view.guides': 'Guides',
  'view.snapping': 'Snapping',
  'view.reference': 'Reference background',
  'view.design': 'Show design values instead of the step 1 hold',

  // Toolbar
  'toolbar.label': 'Tools',
  'toolbar.add': 'Add',
  'toolbar.align': 'Align and distribute',
  'toolbar.view': 'View',
  'tool.text': 'Text',
  'tool.box': 'Box',
  'tool.boxHint': 'Auto-size box: hugs its content with padding (a name bar)',
  'tool.stack': 'Stack',
  'tool.stackHint': 'Stack: lays out its children in a row or column',
  'tool.rect': 'Rectangle',
  'tool.ellipse': 'Ellipse',
  'tool.line': 'Line',
  'tool.group': 'Group (Mod+G)',
  'tool.ungroup': 'Ungroup (Mod+Shift+G)',
  'tool.mask': 'Clip mask (Mod+Alt+G)',
  'align.left': 'Align left',
  'align.hcenter': 'Align centres horizontally',
  'align.right': 'Align right',
  'align.top': 'Align top',
  'align.vmiddle': 'Align middles vertically',
  'align.bottom': 'Align bottom',
  'align.distributeH': 'Distribute horizontally',
  'align.distributeV': 'Distribute vertically',

  // Canvas
  'canvas.region': 'Canvas',
  'canvas.label': 'Canvas, {width} by {height} pixels',
  'canvas.help': 'Click to select, drag to move, Shift-click to add to the selection, double-click to enter a group or edit text. Arrow keys nudge (Shift: 10 px).',
  'canvas.renderError': 'The canvas could not render: {message}',

  // Layers
  'layers.title': 'Layers',
  'layers.label': 'Layers',
  'layers.empty': 'Nothing here yet. Add text, a box or a shape from the toolbar.',
  'layers.hint': 'Drag to reorder or move into a group. Double-click or F2 to rename.',
  'layers.hidden': 'hidden',
  'layers.locked': 'locked',
  'layers.hide': 'Hide',
  'layers.show': 'Show',
  'layers.lock': 'Lock',
  'layers.unlock': 'Unlock',
  'layers.rename': 'Layer name',
  'layers.deleteSelected': 'Delete selected layers',

  // Assets
  'assets.title': 'Assets',
  'assets.kinds': 'Asset types',
  'assets.kind.font': 'Fonts',
  'assets.kind.image': 'Images',
  'assets.kind.video': 'Video',
  'assets.upload': 'Upload',
  'assets.uploading': 'Uploading…',
  'assets.uploadHint': 'Fonts: WOFF2, TTF, OTF (converted to WOFF2). Images: PNG, JPEG, WebP, SVG (sanitised). Video: WebM, MP4.',
  'assets.uploaded': 'Uploaded {names}',
  'assets.uploadFailed': '{name}: {message}',
  'assets.loading': 'Loading the library…',
  'assets.empty': 'Nothing uploaded yet.',
  'assets.dropHint': 'Drop files here to upload.',
  'assets.variable': 'variable',
  'assets.builtin': 'built-in',
  'assets.inUse': 'in use',
  'assets.applyFont': 'Use',
  'assets.applyFontHint': 'Use this font for the selected text',
  'assets.add': 'Add',
  'assets.addNamed': 'Add {name} to the canvas',
  'assets.coverageOk': 'Covers every {lang} character',
  'assets.coverageMissing': 'Missing for {lang}: {chars}',

  // Inspector
  'inspector.graphic': 'Graphic',
  'inspector.graphic.meta': 'Name and description',
  'inspector.graphic.name': 'Name',
  'inspector.graphic.description': 'Description',
  'inspector.graphic.id': 'Graphic id: {id}',
  'inspector.graphic.canvas': 'Canvas',
  'inspector.graphic.rate': 'Frame rate {num}/{den} (change it in the top bar).',
  'inspector.graphic.reference': 'Reference background',
  'inspector.graphic.referenceHint': 'Shown only in the editor, never in the graphic.',
  'inspector.graphic.referenceColor': 'Background colour',
  'inspector.graphic.referenceImage': 'Reference still',
  'inspector.nothingSelected': 'Select something on the canvas or in the layers to edit it.',
  'inspector.multi': '{count} selected',
  'inspector.visibility': 'Visibility',
  'inspector.name': 'Element name',
  'inspector.position': 'Position and size',
  'inspector.inStack': 'Inside a stack: the stack places this element; X and Y nudge it from there.',
  'inspector.x': 'X position',
  'inspector.y': 'Y position',
  'inspector.width': 'Width',
  'inspector.height': 'Height',
  'inspector.thickness': 'Thickness',
  'inspector.auto': 'auto',
  'inspector.rotation': 'Rotation',
  'inspector.opacity': 'Opacity',
  'inspector.scaleX': 'Scale X',
  'inspector.scaleY': 'Scale Y',
  'inspector.anchorH': 'Pin horizontally to',
  'inspector.anchorV': 'Pin vertically to',
  'inspector.anchorHint': 'Pinned to the parent’s edges (top-level elements: the canvas). X/Y offset from there.',
  'anchor.left': 'Left',
  'anchor.center': 'Centre',
  'anchor.right': 'Right',
  'anchor.top': 'Top',
  'anchor.middle': 'Middle',
  'anchor.bottom': 'Bottom',
  'anchor.stretch': 'Stretch',
  'inspector.sizeRules': 'Auto size',
  'inspector.autoWidth': 'Hug content width (auto-size)',
  'inspector.autoWidthText': 'Width follows the text',
  'inspector.autoHeight': 'Hug content height',
  'inspector.minWidth': 'Minimum width',
  'inspector.maxWidth': 'Maximum width',
  'inspector.minHeight': 'Minimum height',
  'inspector.maxHeight': 'Maximum height',
  'inspector.sizeRulesHint': 'An auto-size box grows with its content (plus padding) between the minimum and maximum.',
  'inspector.sizeRulesTextHint': 'With a maximum width the text overflow rule decides what happens to longer text.',
  'inspector.layout': 'Layout',
  'inspector.layoutMode': 'Children',
  'layout.absolute': 'Free (positioned)',
  'layout.stack': 'Stack (flow)',
  'inspector.direction': 'Direction',
  'layout.horizontal': 'Row',
  'layout.vertical': 'Column',
  'inspector.gap': 'Gap',
  'inspector.align': 'Align',
  'inspector.justify': 'Distribute',
  'layout.start': 'Start',
  'layout.center': 'Centre',
  'layout.end': 'End',
  'layout.stretch': 'Stretch',
  'layout.spaceBetween': 'Space between',
  'inspector.padding': 'Padding',
  'inspector.padding.top': 'Padding top',
  'inspector.padding.right': 'Padding right',
  'inspector.padding.bottom': 'Padding bottom',
  'inspector.padding.left': 'Padding left',
  'inspector.padding.top.short': 'T',
  'inspector.padding.right.short': 'R',
  'inspector.padding.bottom.short': 'B',
  'inspector.padding.left.short': 'L',
  'inspector.clip': 'Clip content to the box',
  'inspector.mask': 'Clip mask',
  'inspector.mask.shape': 'Shape',
  'inspector.text': 'Text',
  'inspector.textContent': 'Text',
  'inspector.textBound': 'Text (default of field “{title}”)',
  'inspector.font': 'Font',
  'inspector.weight': 'Weight',
  'inspector.fontSize': 'Font size',
  'inspector.size': 'Size',
  'inspector.tracking': 'Tracking (letter spacing)',
  'inspector.tracking.short': 'Track',
  'inspector.lineHeight': 'Line height',
  'inspector.lineHeight.short': 'Line',
  'inspector.case': 'Case',
  'case.none': 'As typed',
  'case.uppercase': 'UPPERCASE',
  'case.lowercase': 'lowercase',
  'case.capitalize': 'Capitalise',
  'inspector.textAlign': 'Alignment',
  'inspector.italic': 'Italic',
  'inspector.color': 'Colour',
  'inspector.stroke': 'Stroke',
  'inspector.strokeColor': 'Stroke colour',
  'inspector.strokeWidth': 'Stroke width',
  'inspector.shadow': 'Shadow',
  'inspector.shadowColor': 'Shadow colour',
  'inspector.shadowX': 'Shadow X',
  'inspector.shadowY': 'Shadow Y',
  'inspector.shadowBlur': 'Shadow blur',
  'inspector.blur': 'Blur',
  'inspector.blurWarning': 'Blur is expensive at 50 fps and softens keyed edges; keep blurred areas small.',
  'inspector.overflow': 'When text does not fit',
  'inspector.overflowMode': 'Rule',
  'overflow.none': 'Let it overflow',
  'overflow.shrink': 'Shrink to fit',
  'overflow.wrap': 'Wrap to lines',
  'overflow.ellipsis': 'Cut with …',
  'overflow.none.hint': 'Long text runs past the box. Use a rule for text that comes from a field.',
  'overflow.shrink.hint': 'The font size shrinks (down to the minimum) until the text fits the available width.',
  'overflow.wrap.hint': 'The text wraps and is cut after the last line.',
  'overflow.ellipsis.hint': 'One line, cut with an ellipsis.',
  'inspector.minFontSize': 'Minimum font size',
  'inspector.minSize': 'Min',
  'inspector.maxLines': 'Maximum lines',
  'inspector.lines': 'Lines',
  'inspector.shrunkTo': 'Shrunk to {size} px with the current data.',
  'inspector.fillStroke': 'Fill and stroke',
  'inspector.fill': 'Fill',
  'inspector.fillColor': 'Fill colour',
  'fill.solid': 'Solid',
  'fill.gradient': 'Linear gradient',
  'inspector.angle': 'Gradient angle',
  'inspector.stop': 'Colour {n}',
  'inspector.none': 'None',
  'inspector.radius': 'Corner radius',
  'inspector.radiusUniform': 'Same radius for every corner',
  'inspector.radiusPerCorner': 'Radius per corner',
  'inspector.radius.tl': 'Top-left radius',
  'inspector.radius.tr': 'Top-right radius',
  'inspector.radius.br': 'Bottom-right radius',
  'inspector.radius.bl': 'Bottom-left radius',
  'inspector.radius.tl.short': '◤',
  'inspector.radius.tr.short': '◥',
  'inspector.radius.br.short': '◢',
  'inspector.radius.bl.short': '◣',
  'inspector.line': 'Line',
  'inspector.cap': 'Ends',
  'cap.butt': 'Square',
  'cap.round': 'Round',
  'inspector.source': 'Source',
  'inspector.fit': 'Fit',
  'fit.contain': 'Fit inside',
  'fit.cover': 'Fill and crop',
  'fit.fill': 'Stretch',
  'inspector.loop': 'Loop',
  'inspector.fields': 'Fields',
  'inspector.fieldsHint': 'Expose a property as a field so PRISM users can fill it in on the timeline.',
  'inspector.expose': 'Expose as field',
  'inspector.unexpose': 'Remove',
  'inspector.openField': 'Open in the Data panel',
  'expose.text': 'Text content',
  'expose.color': 'Colour',
  'expose.strokeColor': 'Stroke colour',
  'expose.visible': 'Visibility',
  'expose.image': 'Image',
  'expose.opacity': 'Opacity',
  'weight.100': 'Thin',
  'weight.200': 'Extra light',
  'weight.300': 'Light',
  'weight.400': 'Regular',
  'weight.500': 'Medium',
  'weight.600': 'Semibold',
  'weight.700': 'Bold',
  'weight.800': 'Extra bold',
  'weight.900': 'Black',
  'type.text': 'Text',
  'type.rect': 'Rectangle',
  'type.ellipse': 'Ellipse',
  'type.line': 'Line',
  'type.image': 'Image',
  'type.video': 'Video',
  'type.group': 'Group',
  'type.box': 'Auto-size box',
  'type.stack': 'Stack',
  'type.clipMask': 'Clip mask',

  // Data panel
  'data.title.panel': 'Fields and test data',
  'data.hint': 'Test values change the canvas live; they are not saved into the graphic. Defaults are what the graphic shows without data.',
  'data.none': 'No fields yet. Select an element and use “Expose as field” in the Design tab.',
  'data.reset': 'Reset test data',
  'data.testValue': 'Test value',
  'data.useDefault': 'Use default',
  'data.hidden': 'hidden',
  'data.moveUp': 'Move up',
  'data.moveDown': 'Move down',
  'data.title': 'Title',
  'data.key': 'Key (manifest property)',
  'data.default': 'Default',
  'data.maxLength': 'Maximum length',
  'data.multiline': 'Multi-line',
  'data.enum': 'Allowed values',
  'data.enumHint': 'Comma-separated; empty allows any text',
  'data.empty': '(empty)',
  'data.minimum': 'Minimum',
  'data.maximum': 'Maximum',
  'data.choices': 'Images to choose from',
  'data.description': 'Description',
  'data.hiddenLabel': 'Hidden from clip labels in PRISM',
  'data.boundTo': 'Drives: {targets}',
  'data.unbound': 'nothing (bind it from the Design tab)',
  'data.remove': 'Remove field',
  'fieldType.string': 'text',
  'fieldType.number': 'number',
  'fieldType.boolean': 'on/off',
  'fieldType.color': 'colour',
  'fieldType.image-choice': 'image',
  'binding.text': 'text',
  'binding.color': 'colour',
  'binding.strokeColor': 'stroke colour',
  'binding.visible': 'visibility',
  'binding.image': 'image',
  'binding.opacity': 'opacity',

  // Bottom panel and problems
  'bottom.label': 'Problems and timeline',
  'bottom.problems': 'Problems',
  'bottom.timeline': 'Timeline',
  'problems.none': 'No problems with the test data or the defaults.',
  'problems.error': 'Error',
  'problems.warning': 'Warning',
  'problems.info': 'Note',
  'problems.source.test': 'test data',
  'problems.source.default': 'defaults',
  'problems.source.both': 'test data and defaults',
  'problem.text.overflow': '“{name}”: the text does not fit its box',
  'problem.text.truncated': '“{name}”: the text is cut with an ellipsis',
  'problem.text.clamped': '“{name}”: the text has more lines than allowed and is cut',
  'problem.canvas.odd': 'The canvas is {width} × {height}: PRISM’s sequences have even sizes, so it would be scaled by a fraction. Use an even width and height.',
  'problem.canvas.range': 'The canvas is {width} × {height}: PRISM’s sequences are 16–7680 px wide and 16–4320 px high.',
  'problem.safeArea': '“{name}” leaves the graphics-safe area ({margin}% margins)',
  'problem.glyphs': '“{name}”: the font {font} has no glyphs for {chars}',
  'problem.coverage': 'Font {font} is missing {lang} characters: {chars}',
  'problem.field.unbound': 'Field “{title}” drives nothing',
  'problem.field.testValue': 'Test value of “{title}”: {error}',
  'problem.assetMissing': 'Asset {file} is not in the library; export will fail',

  // Dialogs and shortcuts
  'dialog.close': 'Close',
  'shortcuts.title': 'Keyboard shortcuts',
  'shortcuts.group.general': 'General',
  'shortcuts.group.create': 'Add',
  'shortcuts.group.arrange': 'Arrange',
  'shortcuts.group.view': 'View',
  'shortcuts.mouse': 'Mouse: Shift-click adds to the selection, Mod-click selects inside groups, double-click enters a group or edits text, Shift while dragging keeps one axis, Mod while dragging turns snapping off, Shift while resizing keeps proportions, Shift while rotating snaps to 15°. Drag from a ruler to add a guide; drag it back to remove it.',
  'shortcut.undo': 'Undo',
  'shortcut.redo': 'Redo',
  'shortcut.save': 'Save now',
  'shortcut.copy': 'Copy (also between graphics)',
  'shortcut.cut': 'Cut',
  'shortcut.paste': 'Paste',
  'shortcut.duplicate': 'Duplicate',
  'shortcut.delete': 'Delete',
  'shortcut.selectAll': 'Select all (at this level)',
  'shortcut.deselect': 'Select parent / deselect',
  'shortcut.selectChildren': 'Select children / edit text',
  'shortcut.selectParent': 'Select parent',
  'shortcut.export': 'Export package',
  'shortcut.help': 'Show shortcuts',
  'shortcut.addText': 'Add text',
  'shortcut.addRect': 'Add rectangle',
  'shortcut.addEllipse': 'Add ellipse',
  'shortcut.addLine': 'Add line',
  'shortcut.addBox': 'Add auto-size box',
  'shortcut.addStack': 'Add stack',
  'shortcut.group': 'Group',
  'shortcut.ungroup': 'Ungroup',
  'shortcut.mask': 'Clip mask',
  'shortcut.forward': 'Bring forward',
  'shortcut.backward': 'Send backward',
  'shortcut.front': 'Bring to front',
  'shortcut.back': 'Send to back',
  'shortcut.nudge': 'Nudge 1 px',
  'shortcut.nudge10': 'Nudge 10 px',
  'shortcut.selectKey': 'Timeline: select the focused keyframe (Shift adds)',
  'shortcut.moveKeys': 'Timeline: move selected keyframes 1 frame (Shift: 10)',
  'shortcut.stepFrame': 'Timeline: playhead 1 frame back / forward (Shift: 10)',
  'shortcut.tabs': 'Tabs: previous / next tab',
  'shortcut.hide': 'Hide / show',
  'shortcut.lock': 'Lock / unlock',
  'shortcut.alignLeft': 'Align left',
  'shortcut.alignHCenter': 'Align centres',
  'shortcut.alignRight': 'Align right',
  'shortcut.alignTop': 'Align top',
  'shortcut.alignVMiddle': 'Align middles',
  'shortcut.alignBottom': 'Align bottom',
  'shortcut.distributeH': 'Distribute horizontally',
  'shortcut.distributeV': 'Distribute vertically',
  'shortcut.rulers': 'Rulers',
  'shortcut.safe': 'Safe areas',
  'shortcut.cut43': '4:3 centre cut',
  'shortcut.guides': 'Guides',
  'shortcut.snapping': 'Snapping',
  'shortcut.zoomFit': 'Zoom to fit',
  'shortcut.zoom100': 'Zoom to 100 %',
  'anim.section': 'Animation · {phase}',
  'anim.hint': 'Values at the playhead. ◆ adds or removes a keyframe; editing a value keys it.',
  'anim.addKey': 'Add a {property} keyframe at the playhead',
  'anim.removeKey': 'Remove the {property} keyframe at the playhead',
  'anim.prop.x': 'X',
  'anim.prop.y': 'Y',
  'anim.prop.scaleX': 'Scale X',
  'anim.prop.scaleY': 'Scale Y',
  'anim.prop.rotation': 'Rotation',
  'anim.prop.opacity': 'Opacity',
  'anim.prop.width': 'Width',
  'anim.prop.height': 'Height',
  'anim.prop.insetTop': 'Clip top %',
  'anim.prop.insetRight': 'Clip right %',
  'anim.prop.insetBottom': 'Clip bottom %',
  'anim.prop.insetLeft': 'Clip left %',
  'anim.prop.color': 'Colour',
  'anim.prop.blur': 'Blur',
  'anim.prop.letterSpacing': 'Letter spacing',
  'timeline.phases': 'Phases',
  'timeline.phase.in': 'In',
  'timeline.phase.out': 'Out',
  'timeline.phase.update': 'Update',
  'timeline.phase.through': 'Play through',
  'timeline.phase.step': 'To {name}',
  'timeline.phase.loop': 'Loop · {name}',
  'timeline.phase.field': 'Update · {name}',
  'timeline.phase.custom': 'Action · {name}',
  'timeline.stepCount': 'stepCount {count}',
  'timeline.addStep': 'Step',
  'timeline.addAction': 'Custom action',
  'timeline.newAction': 'Custom action',
  'timeline.sting': 'Sting (stepCount 0)',
  'timeline.toStart': 'Go to start',
  'timeline.toEnd': 'Go to end',
  'timeline.prevFrame': 'Previous frame (←, Shift ×10)',
  'timeline.nextFrame': 'Next frame (→, Shift ×10)',
  'timeline.play': 'Play (Space)',
  'timeline.pause': 'Pause (Space)',
  'timeline.duration': 'Phase duration',
  'timeline.durationShort': 'Duration',
  'timeline.autoKey': 'Auto-key',
  'timeline.timeFormat': 'Time display',
  'timeline.format.frames': 'Frames',
  'timeline.format.timecode': 'Timecode',
  'timeline.format.ms': 'Milliseconds',
  'timeline.zoomIn': 'Zoom in (Ctrl/⌘ + wheel)',
  'timeline.zoomOut': 'Zoom out',
  'timeline.removeTrack': 'Remove this track',
  'timeline.empty': 'Select an element and add keyframes (Animation section in the inspector), or apply a preset.',
  'timeline.throughHint': 'Plays In, every step and Out in sequence, as PRISM plays a clip. Open a phase to edit it.',
  'timeline.lanes': 'Keyframes. Click a key to select it, Shift-click to add, drag to move; drag empty space to select several; double-click a track to add a key; Delete removes; Mod+C / Mod+V copy and paste at the playhead.',
  'timeline.keyframe': '{property} keyframe at {time}',
  'timeline.phaseEnd': 'End of the phase',
  'timeline.side': 'Presets, easing and phase settings',
  'timeline.manifest': 'Manifest:',
  'timeline.stagger.char': 'per character',
  'timeline.stagger.word': 'per word',
  'timeline.stagger.line': 'per line',
  'timeline.presets': 'Presets',
  'timeline.preset': 'Preset',
  'timeline.direction': 'Direction',
  'timeline.presetDuration': 'Length',
  'timeline.applyIn': 'Apply (animate in)',
  'timeline.applyOut': 'Apply (animate out)',
  'timeline.presetHint': 'Adds ordinary keyframes for {count} element(s) at the playhead; edit them like any other.',
  'timeline.presetNeedsSelection': 'Select one or more elements first.',
  'timeline.presetNeedsText': 'This preset needs a selected text element.',
  'preset.fade': 'Fade',
  'preset.slide': 'Slide',
  'preset.wipe': 'Wipe',
  'preset.reveal': 'Reveal (from the centre)',
  'preset.scalePop': 'Scale pop',
  'preset.staggerLines': 'Stagger per line',
  'preset.staggerChars': 'Stagger per character',
  'preset.typewriter': 'Typewriter',
  'direction.left': 'Left',
  'direction.right': 'Right',
  'direction.up': 'Up',
  'direction.down': 'Down',
  'timeline.easing': 'Easing',
  'timeline.easingCount': 'Keyframes ({count})',
  'timeline.easingHint': 'The easing applies from each selected keyframe to the next one. Drag the handles for a custom curve.',
  'timeline.curve.label': 'Easing curve cubic-bezier({value})',
  'timeline.curve.handle': 'Control point {n} ({x}, {y}): arrow keys move it, Shift for larger steps',
  'easing.custom': 'Custom curve',
  'easing.linear': 'Linear',
  'easing.ease': 'Ease',
  'easing.easeIn': 'Ease in',
  'easing.easeOut': 'Ease out',
  'easing.easeInOut': 'Ease in-out',
  'easing.easeInQuad': 'Ease in (quad)',
  'easing.easeOutQuad': 'Ease out (quad)',
  'easing.easeInOutQuad': 'Ease in-out (quad)',
  'easing.easeInCubic': 'Ease in (cubic)',
  'easing.easeOutCubic': 'Ease out (cubic)',
  'easing.easeInOutCubic': 'Ease in-out (cubic)',
  'easing.easeInQuart': 'Ease in (quart)',
  'easing.easeOutQuart': 'Ease out (quart)',
  'easing.easeInOutQuart': 'Ease in-out (quart)',
  'easing.easeInExpo': 'Ease in (expo)',
  'easing.easeOutExpo': 'Ease out (expo)',
  'easing.easeInOutExpo': 'Ease in-out (expo)',
  'easing.easeInBack': 'Ease in (back)',
  'easing.easeOutBack': 'Ease out (back)',
  'easing.easeInOutBack': 'Ease in-out (back)',
  'easing.hold': 'Hold (step)',
  'timeline.stingSettings': 'Sting',
  'timeline.autoOutAfter': 'Out starts after (hold)',
  'timeline.stingHint': 'stepCount 0: playAction plays In, holds, then Out automatically.',
  'timeline.stepSettings': 'Step {n}',
  'timeline.stepName': 'Name',
  'timeline.holdLoop': 'Hold loop at this step',
  'timeline.loopHint': 'The loop repeats until the next action and never delays a promise.',
  'timeline.removeStep': 'Remove step',
  'timeline.updateSettings': 'Update transitions',
  'timeline.updateDuration': 'Update duration',
  'timeline.updateEasing': 'Update easing',
  'timeline.autoSizeHint': 'Auto-size boxes animate to their new size over the update duration.',
  'transition.cut': 'Cut',
  'transition.crossfade': 'Crossfade',
  'transition.phase': 'Re-run animation',
  'timeline.noFields': 'No fields yet: expose properties as fields in the Data panel.',
  'timeline.customSettings': 'Custom action',
  'timeline.actionName': 'Name',
  'timeline.actionId': 'Id (customAction id)',
  'timeline.actionIdHint': 'Ids start with a letter: letters, digits, _ and -.',
  'timeline.actionSchema': 'Payload schema (JSON Schema, optional)',
  'timeline.schemaObject': 'The schema must be a JSON object',
  'timeline.removeAction': 'Remove custom action',
};
